import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { cbtAPI } from '../../api/cbt';
import JoinTestModal from '../components/JoinTestModal';
import { Sidebar } from './dashboard_component';

const CbtPassCode = () => {
  const navigate = useNavigate();
  const [testResults, setTestResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [overallStats, setOverallStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPasscodeModal, setShowPasscodeModal] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    fetchAllAnalytics();
  }, []);

  // Filter results whenever searchTerm or testResults changes
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredResults(testResults);
      return;
    }

    const lowerSearch = searchTerm.toLowerCase();
    const filtered = testResults.filter(result => 
      result.cbt_name?.toLowerCase().includes(lowerSearch) ||
      result.cbt_data_id?.toLowerCase().includes(lowerSearch) ||
      result.test_id?.toLowerCase().includes(lowerSearch)
    );
    setFilteredResults(filtered);
  }, [searchTerm, testResults]);

  const fetchAllAnalytics = async () => {
    try {
      setLoading(true);
      const response = await cbtAPI.getAllStudentResultsAnalytics();
      const { overall_statistics, test_results } = response.data;
      setOverallStats(overall_statistics);
      setTestResults(test_results || []);
      setFilteredResults(test_results || []);
    } catch (error) {
      console.error("Error fetching student analytics:", error);
      toast.error("Failed to load your test history");
      setTestResults([]);
      setFilteredResults([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 70) return 'text-green-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPassedBadge = (passed) => {
    return passed
      ? { text: 'Passed', color: 'bg-green-100 text-green-700' }
      : { text: 'Failed', color: 'bg-red-100 text-red-700' };
  };

  const handleViewDetails = (testResult) => {
    navigate(`/participate/result-details/${testResult.internal_test_id}`, {
      state: { testResult }
    });
  };

  const handleToggle = () => setIsCollapsed(!isCollapsed);
  const clearSearch = () => setSearchTerm('');

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
      <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
      
      <div className={`flex-1 transition-all duration-300 pt-16 lg:pt-5 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Welcome Hero */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Welcome Back! 👋</h2>
                  <p className="text-blue-100">Track your progress and join new examinations</p>
                </div>
                <button
                  onClick={() => setShowPasscodeModal(true)}
                  className="mt-4 md:mt-0 bg-white text-blue-600 hover:bg-blue-50 font-semibold py-3 px-6 rounded-xl shadow-md transition-all flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Join New Test
                </button>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Tests Taken</p>
                  <p className="text-2xl font-bold text-gray-800">{loading ? '-' : (overallStats?.total_tests_taken || 0)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Pass Rate</p>
                  <p className="text-2xl font-bold text-gray-800">{loading ? '-' : `${Math.round(overallStats?.pass_rate || 0)}%`}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Best Score</p>
                  <p className="text-2xl font-bold text-gray-800">{loading ? '-' : `${Math.round(Number(overallStats?.best_score) || 0)}%`}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Test History Section */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-emerald-700 px-6 py-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  My Test History
                </h2>
                {/* Search Bar */}
                <div className="relative w-full md:w-80">
                  <input
                    type="text"
                    placeholder="Search by test name, pass code, or test ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 pr-10 rounded-lg border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                  />
                  {searchTerm && (
                    <button
                      onClick={clearSearch}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredResults.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900">
                  {searchTerm ? 'No matching tests found' : 'No test results yet'}
                </h3>
                <p className="text-gray-500 mt-1 mb-4">
                  {searchTerm ? 'Try a different search term' : 'You haven\'t taken any tests yet.'}
                </p>
                {!searchTerm && (
                  <button onClick={() => setShowPasscodeModal(true)} className="bg-blue-600 text-white px-6 py-2 rounded-xl font-medium hover:bg-blue-700 transition">
                    Join Your First Test
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Test Name</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Pass Code</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Score</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredResults.map((result, idx) => {
                        const percentage = Math.round(Number(result.percentage) || 0);
                        const passedBadge = getPassedBadge(result.passed);
                        return (
                          <tr key={result.internal_test_id || idx} className="hover:bg-gray-50 transition">
                            <td className="px-6 py-4">
                              <div className="font-medium text-gray-900">{result.cbt_name || `Test ${idx + 1}`}</div>
                              <div className="text-xs text-gray-400 font-mono">{result.test_id?.slice(0, 8)}...</div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="inline-flex px-2 py-1 rounded-lg bg-blue-50 text-blue-700 text-xs font-mono">{result.cbt_data_id}</span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">{formatDate(result.submitted_at)}</td>
                            <td className="px-6 py-4">
                              <div className="flex flex-col">
                                <span className="font-semibold text-gray-800">{Number(result.total_score).toFixed(1)} pts</span>
                                <span className={`text-sm ${getScoreColor(percentage)}`}>{percentage}%</span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${passedBadge.color}`}>
                                {passedBadge.text}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <button
                                onClick={() => handleViewDetails(result)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-green-600 to-emerald-700 text-white text-sm font-medium rounded-lg hover:from-green-700 transition"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                Details
                              </button>
                             </td>
                           </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden divide-y divide-gray-100">
                  {filteredResults.map((result, idx) => {
                    const percentage = Math.round(Number(result.percentage) || 0);
                    const passedBadge = getPassedBadge(result.passed);
                    return (
                      <div key={result.internal_test_id || idx} className="p-4 space-y-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-gray-900">{result.cbt_name || `Test ${idx + 1}`}</h3>
                            <p className="text-xs text-gray-400 font-mono mt-0.5">{result.test_id}</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${passedBadge.color}`}>{passedBadge.text}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Pass Code:</span>
                          <span className="font-mono text-blue-600">{result.cbt_data_id}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Date:</span>
                          <span>{formatDate(result.submitted_at)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Score:</span>
                          <span className={`font-semibold ${getScoreColor(percentage)}`}>{percentage}% ({Number(result.total_score).toFixed(1)} pts)</span>
                        </div>
                        <button
                          onClick={() => handleViewDetails(result)}
                          className="w-full mt-2 py-2 bg-gradient-to-r from-green-600 to-emerald-700 text-white rounded-lg font-medium text-sm flex items-center justify-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View Details
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Search result summary */}
                {searchTerm && (
                  <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-600">
                    Found {filteredResults.length} result{filteredResults.length !== 1 ? 's' : ''} for "{searchTerm}"
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <JoinTestModal isOpen={showPasscodeModal} onClose={() => setShowPasscodeModal(false)} />
      <ToastContainer position="top-center" autoClose={5000} theme="colored" />
    </div>
  );
};

export { CbtPassCode };